import React, { useState, useEffect, useRef } from 'react';
import LightweightChartV2 from '../components/Charts/LightweightChartV2';
import stockStreamService from '../services/stockStreamService';
import DashboardLayout from '../components/DashboardLayout';
import StockSearchInput from '../components/StockSearchInput';

const ChartV2Page = () => {
    const [symbol, setSymbol] = useState('RELIANCE');
    const [interval, setInterval] = useState('1m');
    const [startDate, setStartDate] = useState(
        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
    );
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    const [chartData, setChartData] = useState([]);
    const [isStreaming, setIsStreaming] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Use a ref to accumulate data efficiently without dependency cycles in stream callback
    const dataRef = useRef(new Map());

    const handleLoadData = () => {
        // Reset data
        setChartData([]);
        dataRef.current = new Map();
        setIsLoading(true);
        setIsStreaming(true);
    };

    const handleSymbolSelect = (stock) => {
        const newSymbol = typeof stock === 'string' ? stock : stock.tradingSymbol;
        setSymbol(newSymbol);
    };

    useEffect(() => {
        if (!isStreaming) return;

        // Convert dates to ISO format expected by backend if needed, 
        // but the input type="date" gives YYYY-MM-DD which is likely fine for the service 
        // if the service constructs Instant.parse (which might need 'T00:00:00Z')
        // Let's ensure strict ISO format for backend Instant.parse()
        const fromIso = new Date(startDate).toISOString();
        const toIso = new Date(endDate).toISOString(); // Or set to end of day if needed

        const cleanup = stockStreamService.streamHistoricalData(
            symbol,
            fromIso,
            toIso,
            interval,
            'NSE',
            'EQUITY',
            (newCandle) => {
                setIsLoading(false);
                // Transform incoming data to lightweight-charts format if necessary
                // Assuming backend sends: { open, high, low, close, volume, timestamp (Instant) }
                // LWC expects: { time, open, high, low, close }
                // Time should be unix timestamp (seconds) or YYYY-MM-DD string

                const candleTime = new Date(newCandle.timestamp).getTime() / 1000;

                // console.log("Received candle:", newCandle.timestamp, candleTime);

                const transformedCandle = {
                    time: candleTime,
                    open: newCandle.openPrice,
                    high: newCandle.highPrice,
                    low: newCandle.lowPrice,
                    close: newCandle.closePrice,
                };

                // Efficiently update ref and state
                // Use Map to deduplicate by time (last update wins)
                dataRef.current.set(candleTime, transformedCandle);

                if (dataRef.current.size % 10 === 0) {
                    console.log(`[ChartV2Page] Collected ${dataRef.current.size} candles via stream`);
                }

                // Convert Map to sorted array
                const sortedData = Array.from(dataRef.current.values()).sort((a, b) => a.time - b.time);

                // Using functional state update to ensure we don't prefer stale closures
                setChartData(sortedData);
            },
            (error) => {
                // If it's a generic error but we handled it in service, logs are there.
                // We stop streaming on error.
                console.warn("Stream stopped due to error/closure.");
                setIsLoading(false);
                setIsStreaming(false);
            },
            () => {
                console.log("Stream complete.");
                setIsLoading(false);
                setIsStreaming(false);
            }
        );

        return () => {
            cleanup();
        };
    }, [isStreaming, symbol, startDate, endDate, interval]);

    return (
        <DashboardLayout>
            <div className="p-6 h-[calc(100vh-6rem)] flex flex-col">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-base-content">Market Analysis V2</h1>
                </div>

                {/* Controls */}
                <div className="bg-base-200 p-4 rounded-lg flex flex-wrap gap-4 items-end mb-4">
                    <div className="w-64">
                        <StockSearchInput onSelect={handleSymbolSelect} initialValue={symbol} />
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Interval</span>
                        </label>
                        <select
                            value={interval}
                            onChange={(e) => setInterval(e.target.value)}
                            className="select select-bordered w-32"
                        >
                            <option value="1m">1 Minute</option>
                            <option value="5m">5 Minutes</option>
                            <option value="15m">15 Minutes</option>
                            <option value="1h">1 Hour</option>
                            <option value="1d">1 Day</option>
                        </select>
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">Start Date</span>
                        </label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="input input-bordered"
                        />
                    </div>

                    <div className="form-control">
                        <label className="label">
                            <span className="label-text">End Date</span>
                        </label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="input input-bordered"
                        />
                    </div>

                    <button
                        onClick={handleLoadData}
                        className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
                        disabled={isStreaming && isLoading}
                    >
                        {isStreaming ? 'Reload' : 'Load Data'}
                    </button>
                </div>

                {/* Chart Container */}
                <div className="flex-grow bg-base-100 rounded-lg shadow-lg overflow-hidden border border-base-300 relative">
                    {chartData.length === 0 && !isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center text-base-content/50">
                            No data loaded. Select parameters and click Load.
                        </div>
                    )}
                    <LightweightChartV2
                        data={chartData}
                        colors={{
                            backgroundColor: '#ffffff', // Could use theme context here
                            textColor: '#333',
                        }}
                    />
                </div>

                <div className="mt-2 text-sm text-base-content/70">
                    Loaded {chartData.length} candles.
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ChartV2Page;
